<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	//Helpers
	//Stores
	//Types
	//Design system components
	import { postReq } from 'src/services/http';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { devices_settings } from 'src/stores';
	import { onMount } from 'svelte';
	import { Button, Input, Text, toast } from '@Shelly/shelly-design-system';
	import {
		getWaveCoverComponentId,
		zwaveCoverGetConfig,
		zwaveCoverSetConfig,
	} from 'src/helpers/z_wave_cover_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { format } from '../../../../services/i18n';

	export let device;

	const id = real_id(device.id);
	const min = 0.1;
	const max = 300;

	let settings = readDeviceSettings(id)['Configuration:0'];
	let maxtimeOpen: number | string = '';
	let maxtimeClose: number | string = '';
	let cacheOpen: number | string = '';
	let cacheClose: number | string = '';
	let loading = false;
	let fetching = false;
	let lastSyncedKey = '';

	$: componentId = getWaveCoverComponentId(device);
	$: coverKey = `cover:${componentId}`;
	$: settings = $devices_settings?.[real_id(id)];
	$: coverConfig = settings?.[coverKey] || {};
	$: syncFromStore(coverConfig);

	const syncFromStore = (config: Record<string, unknown>) => {
		if (loading) {
			return;
		}
		const open = config?.maxtime_open;
		const close = config?.maxtime_close;
		const syncKey = `${open}:${close}`;
		if (syncKey === lastSyncedKey) {
			return;
		}
		if (typeof open === 'number' || typeof open === 'string') {
			maxtimeOpen = open;
			cacheOpen = open;
		}
		if (typeof close === 'number' || typeof close === 'string') {
			maxtimeClose = close;
			cacheClose = close;
		}
		lastSyncedKey = syncKey;
	};

	$: errorMaxtimeOpen =
		maxtimeOpen !== '' && (+maxtimeOpen < min || +maxtimeOpen > max)
			? $format('generic_out_of_range', { default: 'Value out of range' })
			: '';

	$: errorMaxtimeClose =
		maxtimeClose !== '' && (+maxtimeClose < min || +maxtimeClose > max)
			? $format('generic_out_of_range', { default: 'Value out of range' })
			: '';

	const validate = (value: number | string) => {
		let next = +value;
		if (next > max) {
			next = max;
		}
		if (next < min) {
			next = min;
		}
		return next;
	};

	const setMovementLimit = async () => {
		if (cacheClose === maxtimeClose && cacheOpen === maxtimeOpen) {
			return;
		}
		loading = true;
		maxtimeClose = validate(maxtimeClose);
		maxtimeOpen = validate(maxtimeOpen);

		const res = await zwaveCoverSetConfig(device, {
			maxtime_open: +maxtimeOpen,
			maxtime_close: +maxtimeClose,
		});

		if (res.isok) {
			toast.open({
				type: 'success',
				title: $format('setting_scc_saved_toast'),
			});
			cacheClose = maxtimeClose;
			cacheOpen = maxtimeOpen;
			lastSyncedKey = `${maxtimeOpen}:${maxtimeClose}`;
		} else {
			toast.open({ type: 'danger', title: $format('error_global') });
			maxtimeClose = cacheClose;
			maxtimeOpen = cacheOpen;
		}
		loading = false;
	};

	onMount(async () => {
		if (
			typeof coverConfig?.maxtime_open !== 'undefined' &&
			typeof coverConfig?.maxtime_close !== 'undefined'
		) {
			return;
		}
		fetching = true;
		await zwaveCoverGetConfig(device);
		fetching = false;
	});
</script>

<Text styleType="body-small" class="mb-s">
	{$format('movement_limit_open_description', {
		values: { value: maxtimeOpen, min: min, max: max },
		default:
			'Limit the time for moving in a direction in seconds. Values must be from ${min} to ${max}. Note: Setting this to low value may affect calibration.',
	})}
</Text>

{#if fetching && maxtimeOpen === '' && maxtimeClose === ''}
	<Text styleType="body-small" class="mb-s">
		{$format('loading', { default: 'Loading…' })}
	</Text>
{/if}

<Text styleType="body-small" class="mb-s">
	{$format('option_on_opening', { default: 'While opening' })}
</Text>
<Input
	class="mb-s"
	size="m"
	bind:value={maxtimeOpen}
	type="number"
	{min}
	{max}
	error={errorMaxtimeOpen}
	info={$format('movement_time_seconds_hint', {
		default: 'Number of seconds (Min 0.1s, Max 300s) the cover is allowed to move when opening.',
	})}
	step={0.1}
	on:enter={() => {
		maxtimeOpen = validate(maxtimeOpen);
	}}
	on:enter={setMovementLimit}
	id="input-wave-maxtime-open"
	aria-label={$format('option_on_opening', { default: 'While opening' })}
/>

<Text styleType="body-small" class="mb-s">
	{$format('option_on_closing', { default: 'While closing' })}
</Text>
<Input
	class="mb-s"
	size="m"
	bind:value={maxtimeClose}
	type="number"
	{min}
	{max}
	error={errorMaxtimeClose}
	info={$format('movement_time_seconds_hint_close', {
		default: 'Number of seconds (Min 0.1s, Max 300s) the cover is allowed to move when closing.',
	})}
	step={0.1}
	on:enter={() => {
		maxtimeClose = validate(maxtimeClose);
	}}
	on:enter={setMovementLimit}
	id="input-wave-maxtime-close"
	aria-label={$format('option_on_closing', { default: 'While closing' })}
/>

<Button class="mt-s" {loading} size="m" width="full" on:click={setMovementLimit}>
	{$format('button_save')}
</Button>
